/**
 * Everybody's chance of winning, once the cards are face up.
 *
 * The moment an all-in is called and the hands are tabled, the decisions are
 * over and only the arithmetic is left. Every broadcast and every serious room
 * puts the numbers on screen at that point, because it is the one time they
 * can be shown without helping anybody: nothing is hidden any more, and there
 * is nothing left to act on.
 *
 * That last part is why this is safe to compute here, and why it is written to
 * refuse the moment it is not. It runs only when every player still in the
 * hand has shown, which the server only does at a showdown or an all-in
 * run-out. If any contender's cards are still face down — because the hand is
 * live and somebody has a decision to make — there is nothing to work out and
 * this returns nothing. A percentage on screen while betting is still open
 * would be the room telling one player what another is holding.
 */

import { cardFromString, equityBetween, seededRandom } from '@pf/engine';
import type { TableSnapshot } from '@pf/protocol';

export interface WinChance {
  readonly seat: number;
  /** Percentage of the pot this hand takes on average, ties included. */
  readonly share: number;
  /** How often it wins outright. */
  readonly win: number;
  readonly tie: number;
}

/**
 * Null unless the hand is over the decisions and under the cards.
 *
 * Samples rather than exhaustion: with two cards to come there are 1,081
 * boards and it could be counted exactly, but the same code has to answer on
 * the flop of a three-way pot where it cannot, and one method that is always
 * fast enough beats two that disagree at the edges.
 */
export function winChances(snapshot: TableSnapshot): WinChance[] | null {
  const contenders = snapshot.seats.filter(
    (seat) => seat.status === 'active' || seat.status === 'all-in',
  );
  if (contenders.length < 2) return null;

  // The whole safety condition, in one line: every hand still live is face up.
  if (!contenders.every((seat) => seat.cards !== null && seat.cards.length === 2)) return null;

  // Nothing to work out once the last card is down — the winner is not a
  // probability at that point, it is a fact, and the table says so elsewhere.
  if (snapshot.board.length >= 5) return null;

  const board = snapshot.board.map(cardFromString);
  const hands = contenders.map((seat) => (seat.cards ?? []).map(cardFromString));

  // Seeded on the hand rather than the clock, so the same spot shows the same
  // numbers to everyone watching it and does not jitter between renders.
  const random = seededRandom(`${snapshot.tableId}:${snapshot.handNumber}:allin`);

  // One call for the whole table rather than one per player: every hand is
  // measured against the same run-outs, so the shares add to a hundred instead
  // of to whatever four independent simulations happened to produce.
  const chances = equityBetween(hands, board, 4_000, random);

  return contenders.map((seat, index) => {
    const result = chances[index] as { share: number; win: number; tie: number };
    return {
      seat: seat.seat,
      share: Math.round(result.share * 1000) / 10,
      win: Math.round(result.win * 1000) / 10,
      tie: Math.round(result.tie * 1000) / 10,
    };
  });
}
